// ============================================================================
// Vista: pagos — Panel Cliente
// Envío de comprobante de pago + historial.
// Renovación: paga el plan actual.
// Prueba terminada/sin plan pagado: elige un plan pagado y envía comprobante.
// "CAMBIAR DE PLAN" para cuentas activas sigue siendo un flujo separado.
// ============================================================================

import { supabaseClient } from '@plataforma/supabase-client';
import { mostrarToast } from '@plataforma/ui';
import { escapeAttr } from '@plataforma/utils';

import { eliminarComprobante, subirComprobante } from '../api/comprobantes.js';
import { listarMetodosPagoActivos, obtenerConfigPagos } from '../api/pagos.js';
import { obtenerEstadoSesion, recargarEmpresa } from '../estado-sesion.js';
import { exigirSesionEmpresa } from '../guardas.js';

import { montarShell } from './shell.js';

function moneda(valor) {
  return new Intl.NumberFormat('es-PE', {
    style: 'currency',
    currency: 'PEN',
    minimumFractionDigits: 2,
  }).format(Number(valor ?? 0));
}

function fecha(valor) {
  if (!valor) return '—';
  return new Date(valor).toLocaleDateString('es-PE');
}

function fechaHora(valor) {
  if (!valor) return '—';
  return new Date(valor).toLocaleString('es-PE');
}

const ESTADOS_PAGO = {
  pendiente: { etiqueta: 'Pendiente', tono: 'alerta' },
  pagado: { etiqueta: 'Pagado', tono: 'exito' },
  aprobado: { etiqueta: 'Aprobado', tono: 'exito' },
  rechazado: { etiqueta: 'Rechazado', tono: 'peligro' },
  vencido: { etiqueta: 'Vencido', tono: 'peligro' },
  anulado: { etiqueta: 'Anulado', tono: 'neutral' },
  reembolsado: { etiqueta: 'Reembolsado', tono: 'neutral' },
};

function badge(estado) {
  const meta = ESTADOS_PAGO[estado] ?? { etiqueta: estado, tono: 'neutral' };
  return `<pf-badge-estado tono="${meta.tono}">${meta.etiqueta}</pf-badge-estado>`;
}

const MESES_OPCIONES = [1, 3, 6, 12, 24];

function esPlanDePago(plan) {
  return Number(plan?.precio_mensual ?? 0) > 0;
}

function requiereSeleccionPlanPago(empresa) {
  return empresa?.estado === 'prueba_terminada' || !esPlanDePago(empresa?.plan);
}

function opcionesPlanesPago(planes, planSeleccionadoId = '') {
  return (planes ?? [])
    .map(
      (plan) => `
        <option value="${plan.id}" ${plan.id === planSeleccionadoId ? 'selected' : ''}>
          ${plan.nombre} - ${moneda(plan.precio_mensual)}/mes
        </option>`,
    )
    .join('');
}

async function copiarTexto(texto) {
  if (!texto) return;
  try {
    await navigator.clipboard.writeText(texto);
  } catch {
    try {
      const zona = document.createElement('textarea');
      zona.value = texto;
      zona.setAttribute('readonly', '');
      zona.style.position = 'fixed';
      zona.style.left = '-9999px';
      document.body.appendChild(zona);
      zona.select();
      document.execCommand('copy');
      document.body.removeChild(zona);
    } catch {
      return;
    }
  }
  mostrarToast('Copiado al portapapeles.', { tono: 'exito', duracionMs: 1800 });
}

// Titular efectivo de un método: prioriza `titular_personalizado`, sino el
// titular general de la configuración global.
function titularMetodo(metodo, config) {
  return metodo.titular_personalizado || config?.titular_general || '';
}

// Si todos los métodos activos comparten el mismo titular, devuelve ese
// titular (para mostrarlo de forma destacada sin repetirlo por método).
function beneficiarioCompartido(metodos, config) {
  const tienenTitular = metodos.filter((m) => titularMetodo(m, config));
  if (tienenTitular.length === 0) return '';
  const primero = titularMetodo(tienenTitular[0], config);
  if (!primero) return '';
  // Todos los métodos con titular deben coincidir con el primero.
  const todosCoinciden = tienenTitular.every((m) => titularMetodo(m, config) === primero);
  return todosCoinciden ? primero : '';
}

function renderDetalleMetodo(metodo, config, beneficiarioCompartidoValor) {
  const esBilletera = metodo.tipo === 'billetera';
  const numero = metodo.numero || '';
  const cci = metodo.cci || '';
  const titular = titularMetodo(metodo, config);
  // No repetir el titular si ya se mostró de forma destacada en la parte alta.
  const valorTitular = beneficiarioCompartidoValor ? '—' : titular || '—';

  return `
    <div class="pf-pago-detail">
      <div class="pf-pago-detail__encabezado">
        <span class="pf-pago-detail__nombre">${escapeAttr(metodo.nombre)}</span>
        <span class="pf-pago-detail__tipo">${esBilletera ? 'Billetera digital' : 'Transferencia bancaria'}</span>
      </div>

      ${
        esBilletera
          ? `
        ${
          metodo.qr_url
            ? `
          <div class="pf-pago-detail__qr">
            <img src="${escapeAttr(metodo.qr_url)}" alt="Código QR ${escapeAttr(metodo.nombre)}" />
          </div>`
            : ''
        }
        <div class="pf-pago-detail__campo">
          <span class="pf-pago-detail__etiqueta">Nombre del método</span>
          <strong>${escapeAttr(metodo.nombre)}</strong>
        </div>
        <div class="pf-pago-detail__campo">
          <span class="pf-pago-detail__etiqueta">Titular</span>
          <div class="pf-pago-detail__valor">
            <strong>${escapeAttr(valorTitular)}</strong>
          </div>
        </div>
        <div class="pf-pago-detail__campo">
          <span class="pf-pago-detail__etiqueta">Número de celular</span>
          <div class="pf-pago-detail__valor">
            <code>${escapeAttr(numero || '—')}</code>
            ${numero ? `<button type="button" class="pf-boton pf-boton--secundario pf-boton--chico pf-pago__copiar" data-copiar="${escapeAttr(numero)}">Copiar número</button>` : ''}
          </div>
        </div>
        `
          : `
        <div class="pf-pago-detail__campo">
          <span class="pf-pago-detail__etiqueta">Nombre del banco</span>
          <strong>${escapeAttr(metodo.nombre)}</strong>
        </div>
        <div class="pf-pago-detail__campo">
          <span class="pf-pago-detail__etiqueta">Titular</span>
          <div class="pf-pago-detail__valor">
            <strong>${escapeAttr(valorTitular)}</strong>
          </div>
        </div>
        <div class="pf-pago-detail__campo">
          <span class="pf-pago-detail__etiqueta">Número de cuenta</span>
          <div class="pf-pago-detail__valor">
            <code>${escapeAttr(numero || '—')}</code>
            ${numero ? `<button type="button" class="pf-boton pf-boton--secundario pf-boton--chico pf-pago__copiar" data-copiar="${escapeAttr(numero)}">Copiar</button>` : ''}
          </div>
        </div>
        <div class="pf-pago-detail__campo">
          <span class="pf-pago-detail__etiqueta">CCI</span>
          <div class="pf-pago-detail__valor">
            <code>${escapeAttr(cci || '—')}</code>
            ${cci ? `<button type="button" class="pf-boton pf-boton--secundario pf-boton--chico pf-pago__copiar" data-copiar="${escapeAttr(cci)}">Copiar CCI</button>` : ''}
          </div>
        </div>
        `
      }

      ${metodo.instrucciones ? `<p class="pf-pago-detail__instrucciones">${escapeAttr(metodo.instrucciones)}</p>` : ''}
    </div>
  `;
}

function construirInfoPlan(empresa) {
  const plan = empresa?.plan;
  const estado = empresa?.estado;

  if (estado === 'prueba_terminada') {
    const nombrePlan = plan?.nombre
      ? `Tu último plan fue ${plan.nombre}.`
      : 'No hay un plan activo.';
    return `
      <div class="pf-campo__error" style="margin:0;">
        <strong>Tu prueba terminó.</strong>
        <span>${nombrePlan} Elige un plan pagado y envía el comprobante para solicitar la activación.</span>
      </div>
    `;
  }

  if (!plan) {
    return `<p class="pf-texto-suave">No tienes un plan activo. Elige un plan pagado para continuar.</p>`;
  }

  const precio = moneda(plan.precio_mensual);
  const fin = empresa?.periodo_activo_fin;

  const estadoLabels = {
    en_prueba: 'En periodo de prueba',
    activa: 'Activa',
    vencida: 'Vencida — pendiente de renovación',
    suspendida: 'Suspendida por falta de pago',
  };

  return `
    <div class="pf-fila" style="gap:16px; flex-wrap:wrap;">
      <div style="flex:1; min-width:140px;">
        <div style="font-size:0.75rem; color:var(--pf-color-texto-terciario, #94a3b8); text-transform:uppercase; letter-spacing:0.05em; margin-bottom:2px;">Plan actual</div>
        <div style="font-size:1.1rem; font-weight:600;">${plan.nombre}</div>
      </div>
      <div style="flex:1; min-width:120px;">
        <div style="font-size:0.75rem; color:var(--pf-color-texto-terciario, #94a3b8); text-transform:uppercase; letter-spacing:0.05em; margin-bottom:2px;">Precio mensual</div>
        <div style="font-size:1.1rem; font-weight:600;">${precio}</div>
      </div>
      <div style="flex:1; min-width:140px;">
        <div style="font-size:0.75rem; color:var(--pf-color-texto-terciario, #94a3b8); text-transform:uppercase; letter-spacing:0.05em; margin-bottom:2px;">Estado</div>
        <div style="font-size:0.875rem;">${estadoLabels[estado] ?? estado}</div>
      </div>
      ${
        fin
          ? `
      <div style="flex:1; min-width:140px;">
        <div style="font-size:0.75rem; color:var(--pf-color-texto-terciario, #94a3b8); text-transform:uppercase; letter-spacing:0.05em; margin-bottom:2px;">Vence el</div>
        <div style="font-size:1.1rem; font-weight:600;">${fecha(fin)}</div>
      </div>`
          : ''
      }
    </div>
    ${
      empresa.plan_pendiente
        ? `
    <div style="margin-top:12px; padding:10px 14px; border-radius:8px; background:var(--pf-color-info-bg, #eff6ff); border:1px solid var(--pf-color-info-border, #bfdbfe); font-size:0.875rem;">
      <span style="font-weight:600; color:var(--pf-color-info-text, #1e40af);">Próximo plan:</span>
      <span style="color:var(--pf-color-info-text, #1e40af);"> ${empresa.plan_pendiente.nombre} · ${moneda(empresa.plan_pendiente.precio_mensual)}/mes</span>
      <span style="color:var(--pf-color-texto-suave, #94a3b8); font-size:0.75rem;"> — aplica al vencimiento del período actual</span>
    </div>`
        : ''
    }
    <div style="margin-top:12px; display:flex; gap:8px; align-items:center;">
      ${
        estado === 'vencida'
          ? '<span class="pf-texto-suave pf-texto-sm">Renueva este plan enviando tu comprobante.</span>'
          : '<a href="#/cambiar-plan" class="pf-boton pf-boton--secundario pf-boton--chico" style="font-size:0.75rem;">Cambiar de plan</a>'
      }
    </div>
  `;
}

// Estado del comprobante a nivel de módulo: sobrevive a re-render de la ruta
// en móviles (Android) cuando el navegador limpia el input[type=file].
let archivoComprobantePago = null;
let urlVistaPreviaComprobantePago = null;

function liberarVistaPreviaComprobantePago() {
  if (!urlVistaPreviaComprobantePago) return;
  URL.revokeObjectURL(urlVistaPreviaComprobantePago);
  urlVistaPreviaComprobantePago = null;
}

function pintarComprobantePago(contenido) {
  const archivo = archivoComprobantePago;
  const panel = contenido.querySelector('#pf-pago-comprobante-seleccionado');
  const info = contenido.querySelector('#pf-pago-comprobante-info');
  const vista = contenido.querySelector('#pf-pago-comprobante-vista');
  if (!panel || !info || !vista) return;

  liberarVistaPreviaComprobantePago();
  if (!archivo) {
    panel.hidden = true;
    vista.replaceChildren();
    return;
  }

  info.textContent = `${archivo.name} · ${(archivo.size / (1024 * 1024)).toFixed(1)} MB`;
  const tipo =
    String(archivo.type || '').toLowerCase() ||
    ({ jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp' }[
      String(archivo.name).split('.').pop()?.toLowerCase()
    ] ??
      '');
  if (tipo.startsWith('image/')) {
    urlVistaPreviaComprobantePago = URL.createObjectURL(archivo);
    const imagen = document.createElement('img');
    imagen.src = urlVistaPreviaComprobantePago;
    imagen.alt = 'Vista previa del comprobante';
    vista.replaceChildren(imagen);
  } else {
    vista.replaceChildren();
  }
  panel.hidden = false;
}

export async function vistaPagos(contenedorRaiz) {
  if (!exigirSesionEmpresa()) return;

  const contenido = montarShell(contenedorRaiz, '/pagos');
  const empresa = obtenerEstadoSesion().empresa;
  const empresaId = empresa?.id;

  if (!empresaId) {
    contenido.innerHTML = `<p class="pf-campo__error">No se pudo cargar la información.</p>`;
    return;
  }

  const debeElegirPlan = requiereSeleccionPlanPago(empresa);
  const [planesRes, metodosRes, configRes] = await Promise.all([
    debeElegirPlan
      ? supabaseClient()
          .from('plan')
          .select('id, nombre, precio_mensual')
          .eq('estado', 'activo')
          .gt('precio_mensual', 0)
          .order('precio_mensual', { ascending: true })
      : Promise.resolve({ data: [], error: null }),
    listarMetodosPagoActivos(),
    obtenerConfigPagos(),
  ]);

  const { data: planesPago, error: errorPlanes } = planesRes;
  const { data: metodos, error: errorMetodos } = metodosRes;
  const config = configRes?.data ?? { titular_general: '' };

  const metodosActivos = metodos ?? [];
  const errorMetodosCarga = errorMetodos;
  const beneficiario = beneficiarioCompartido(metodosActivos, config);

  const opcionesMetodoPago = metodosActivos
    .map((m) => `<option value="${escapeAttr(m.slug)}">${escapeAttr(m.nombre)}</option>`)
    .join('');

  const planesDisponibles = planesPago ?? [];
  const planInicial = debeElegirPlan ? planesDisponibles[0] : empresa?.plan;

  contenido.innerHTML = `
    <div class="pf-page-header">
      <div>
        <h1 class="pf-page-header__titulo">Pagos</h1>
        <p class="pf-texto-suave">Enviar comprobante de pago y ver historial.</p>
      </div>
    </div>

    <div class="pf-dashboard__seccion" id="pf-info-plan">
      <div class="pf-tarjeta" style="padding:16px;">
        <h2 style="margin:0 0 12px; font-size:0.875rem; font-weight:600; text-transform:uppercase; letter-spacing:0.05em; color:var(--pf-color-texto-terciario, #94a3b8);">Tu plan</h2>
        ${construirInfoPlan(empresa)}
      </div>
    </div>

    <div class="pf-dashboard__seccion" id="pf-form-envio">
      <div class="pf-tarjeta" style="padding:24px;">
        <h2 style="margin:0 0 16px; font-size:1rem; font-weight:600;">Enviar comprobante de pago</h2>
        <form id="pf-form-pago" style="display:flex;flex-direction:column;gap:12px;">
          ${
            debeElegirPlan
              ? `
          <div class="pf-campo">
            <label for="pf-pago-plan">Plan a contratar</label>
            ${
              errorPlanes
                ? `
              <p class="pf-campo__error">No se pudieron cargar los planes disponibles.</p>
            `
                : planesDisponibles.length === 0
                  ? `
              <p class="pf-campo__error">No hay planes de pago disponibles en este momento.</p>
            `
                  : `
              <select id="pf-pago-plan" required>
                ${opcionesPlanesPago(planesDisponibles, planInicial?.id)}
              </select>
            `
            }
          </div>`
              : ''
          }
          <div class="pf-fila" style="gap:12px;">
            <div class="pf-campo" style="flex:1;">
              <label for="pf-pago-meses">Meses a pagar</label>
              <select id="pf-pago-meses" required>
                ${MESES_OPCIONES.map((m) => `<option value="${m}">${m} mes${m > 1 ? 'es' : ''}</option>`).join('')}
              </select>
            </div>
            <div class="pf-campo" style="flex:1;">
              <label for="pf-pago-metodo">Método de pago</label>
              ${
                errorMetodosCarga
                  ? `
                <p class="pf-campo__error">No se pudieron cargar los métodos de pago.</p>
              `
                  : metodosActivos.length === 0
                    ? `
                <p class="pf-campo__error">No hay métodos de pago disponibles en este momento.</p>
              `
                    : `
                <select id="pf-pago-metodo" required>
                  ${opcionesMetodoPago}
                </select>
              `
              }
            </div>
          </div>
          ${
            beneficiario
              ? `
          <div class="pf-pago-beneficiario">
            <span class="pf-pago-beneficiario__etiqueta">Beneficiario</span>
            <strong class="pf-pago-beneficiario__nombre">${escapeAttr(beneficiario)}</strong>
          </div>
          `
              : ''
          }
          ${
            metodosActivos.length > 0
              ? `
          <div class="pf-pago-seccion">
            <h4 class="pf-pago-seccion__titulo">Datos para realizar el pago</h4>
            <div id="pf-pago-detalle"></div>
          </div>
          `
              : ''
          }
          <div class="pf-campo">
            <label>Monto estimado</label>
            <div id="pf-pago-monto-est" style="padding:8px 0; font-size:1.1rem; font-weight:600; color:var(--pf-color-primario, #2563eb);">
              S/ 0.00
            </div>
          </div>
          <div class="pf-campo">
            <label for="pf-pago-comprobante">Comprobante (imagen o PDF)</label>
            <input id="pf-pago-comprobante" type="file" accept="image/*,.pdf" />
            <div id="pf-pago-comprobante-seleccionado" class="pf-pago-comprobante" hidden>
              <div id="pf-pago-comprobante-vista" class="pf-pago-comprobante__vista"></div>
              <p id="pf-pago-comprobante-info" class="pf-texto-suave"></p>
              <button type="button" class="pf-boton pf-boton--secundario pf-boton--chico" id="pf-pago-comprobante-quitar">Quitar archivo</button>
            </div>
          </div>
          <div class="pf-fila" style="justify-content:flex-end;gap:8px;">
            <button type="submit" class="pf-boton pf-boton--primario" id="pf-pago-enviar" ${debeElegirPlan && (errorPlanes || planesDisponibles.length === 0) ? 'disabled' : ''}>Enviar pago</button>
          </div>
        </form>
      </div>
    </div>

    <div class="pf-dashboard__seccion">
      <div class="pf-tarjeta" style="padding:24px;">
        <h2 style="margin:0 0 16px; font-size:1rem; font-weight:600;">Historial de pagos</h2>
        <div id="pf-pagos-lista">
          <p class="pf-texto-suave">Cargando...</p>
        </div>
      </div>
    </div>
  `;

  // ── Actualizar monto estimado al cambiar meses ────────────────────────
  const selMeses = contenido.querySelector('#pf-pago-meses');
  const selPlan = contenido.querySelector('#pf-pago-plan');
  const montoEst = contenido.querySelector('#pf-pago-monto-est');

  function planSeleccionado() {
    if (!debeElegirPlan) return empresa?.plan ?? null;
    const planId = selPlan?.value ?? '';
    return planesDisponibles.find((p) => p.id === planId) ?? null;
  }

  function actualizarMonto() {
    const meses = Number(selMeses.value) || 1;
    const plan = planSeleccionado() ?? planInicial;
    montoEst.textContent = moneda(Number(plan?.precio_mensual ?? 0) * meses);
  }

  selMeses.addEventListener('change', actualizarMonto);
  selPlan?.addEventListener('change', actualizarMonto);
  actualizarMonto();

  // ── Datos para realizar el pago (dinámicos) ──────────────────────────
  const selMetodo = contenido.querySelector('#pf-pago-metodo');

  function metodoSeleccionado() {
    if (!selMetodo) return null;
    return metodosActivos.find((m) => m.slug === selMetodo.value) ?? null;
  }

  function renderDetallePago() {
    const destino = contenido.querySelector('#pf-pago-detalle');
    if (!destino) return;
    const metodo = metodoSeleccionado();
    if (!metodo) {
      destino.innerHTML =
        '<p class="pf-texto-suave">Selecciona un método de pago para ver sus datos.</p>';
      return;
    }
    destino.innerHTML = renderDetalleMetodo(metodo, config, beneficiario);
    destino.querySelectorAll('[data-copiar]').forEach((btn) => {
      btn.addEventListener('click', () => copiarTexto(btn.dataset.copiar));
    });
  }

  selMetodo?.addEventListener('change', () => {
    renderDetallePago();
    // Refrescar botón de envío por si el método no está disponible.
    const btn = contenido.querySelector('#pf-pago-enviar');
    if (btn) btn.disabled = false;
  });

  if (metodosActivos.length > 0) renderDetallePago();

  // Capturar comprobante a nivel de módulo (sobrevive a limpieza del input en Android).
  const inputComprobante = contenido.querySelector('#pf-pago-comprobante');
  const capturarComprobante = () => {
    const file = inputComprobante?.files?.[0] ?? null;
    // Android puede emitir un evento tardío con files vacío al cerrar el
    // selector. Mantener la última selección válida hasta que se quite a mano.
    if (file) {
      archivoComprobantePago = file;
      pintarComprobantePago(contenido);
    }
  };
  inputComprobante?.addEventListener('change', capturarComprobante);
  inputComprobante?.addEventListener('input', capturarComprobante);
  contenido.querySelector('#pf-pago-comprobante-quitar')?.addEventListener('click', () => {
    archivoComprobantePago = null;
    if (inputComprobante) inputComprobante.value = '';
    pintarComprobantePago(contenido);
  });
  if (archivoComprobantePago) pintarComprobantePago(contenido);

  // ── Handle submit ────────────────────────────────────────────────────
  contenido.querySelector('#pf-form-pago').addEventListener('submit', async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const btn = contenido.querySelector('#pf-pago-enviar');
    btn.disabled = true;
    btn.textContent = 'Enviando...';

    try {
      const file =
        archivoComprobantePago || contenido.querySelector('#pf-pago-comprobante')?.files?.[0];
      if (!file) {
        mostrarToast('Seleccione un comprobante.', { tono: 'alerta' });
        return;
      }

      const planPago = planSeleccionado();
      if (!planPago?.id || !esPlanDePago(planPago)) {
        mostrarToast('Elige un plan de pago para continuar.', { tono: 'alerta' });
        return;
      }

      const metodoPago = selMetodo?.value ?? '';
      if (!metodoPago) {
        mostrarToast('No hay métodos de pago disponibles en este momento.', { tono: 'alerta' });
        return;
      }

      // Subir comprobante via Edge Function (service_role) — el cliente nunca
      // escribe directo en Storage (bucket privado). Devuelve el PATH.
      const { ruta, error: uploadError } = await subirComprobante(file);
      if (uploadError || !ruta) {
        mostrarToast(
          `No se pudo subir el comprobante: ${uploadError?.message ?? 'intente nuevamente.'}`,
          { tono: 'peligro' },
        );
        return;
      }

      // Enviar pago. Se guarda el PATH privado del comprobante; la RPC calcula
      // el monto desde el plan indicado y crea el pago pendiente.
      const { error: rpcError } = await supabaseClient().rpc('rpc_enviar_pago', {
        p_empresa_id: empresaId,
        p_meses_solicitados: Number(selMeses.value),
        p_metodo_pago: metodoPago,
        p_comprobante_url: ruta,
        p_plan_id: planPago.id,
      });

      if (rpcError) {
        // Limpiar el objeto subido si falla la creación del pago (mejor esfuerzo).
        await eliminarComprobante(ruta);
        throw rpcError;
      }

      mostrarToast('Pago enviado correctamente. Será revisado por el equipo.', { tono: 'exito' });
      archivoComprobantePago = null;
      liberarVistaPreviaComprobantePago();
      contenido.querySelector('#pf-form-pago').reset();
      pintarComprobantePago(contenido);
      actualizarMonto();
      await recargarEmpresa();
      await cargarHistorial(contenido, empresaId);
    } catch (err) {
      console.error('[vistaPagos] Error enviando pago:', err);
      try {
        mostrarToast(`Error: ${err.message}`, { tono: 'peligro' });
      } catch {
        /* ignore */
      }
    } finally {
      btn.disabled = false;
      btn.textContent = 'Enviar pago';
    }
    return false;
  });

  await cargarHistorial(contenido, empresaId);
}

async function cargarHistorial(contenido, empresaId) {
  const destino = contenido.querySelector('#pf-pagos-lista');
  destino.innerHTML = '<p class="pf-texto-suave">Cargando...</p>';

  const { data, error } = await supabaseClient()
    .from('pago')
    .select(
      'id, periodo, monto, estado, metodo_pago, comprobante_url, periodo_inicio, periodo_fin, meses_solicitados, creado_en, revisado_en, motivo_rechazo, plan ( nombre )',
    )
    .eq('empresa_id', empresaId)
    .order('creado_en', { ascending: false })
    .limit(50);

  if (error) {
    destino.innerHTML = '<p class="pf-campo__error">No se pudo cargar el historial.</p>';
    return;
  }

  if (!data || data.length === 0) {
    destino.innerHTML = '<p class="pf-texto-suave">No hay pagos registrados.</p>';
    return;
  }

  destino.innerHTML = `
    <div style="overflow-x:auto;">
      <table style="width:100%; border-collapse:collapse; font-size:0.875rem;">
        <thead>
          <tr style="border-bottom:1px solid var(--pf-color-borde, #e5e7eb); text-align:left;">
            <th style="padding:8px 12px;">Plan</th>
            <th style="padding:8px 12px;">Meses</th>
            <th style="padding:8px 12px;">Monto</th>
            <th style="padding:8px 12px;">Estado</th>
            <th style="padding:8px 12px;">Método</th>
            <th style="padding:8px 12px;">Período</th>
            <th style="padding:8px 12px;">Enviado</th>
            <th style="padding:8px 12px;">Revisado</th>
            <th style="padding:8px 12px;">Comprobante</th>
          </tr>
        </thead>
        <tbody>
          ${data
            .map(
              (p) => `
            <tr style="border-bottom:1px solid var(--pf-color-borde, #e5e7eb);">
              <td style="padding:8px 12px;">${p.plan?.nombre ?? '—'}</td>
              <td style="padding:8px 12px;">${p.meses_solicitados ?? 1}</td>
              <td style="padding:8px 12px;">${moneda(p.monto)}</td>
              <td style="padding:8px 12px;">${badge(p.estado)}</td>
              <td style="padding:8px 12px;">${p.metodo_pago ?? '—'}</td>
              <td style="padding:8px 12px;">${p.periodo_inicio ? `${fecha(p.periodo_inicio)} – ${fecha(p.periodo_fin)}` : '—'}</td>
              <td style="padding:8px 12px;">${fechaHora(p.creado_en)}</td>
              <td style="padding:8px 12px;">${fechaHora(p.revisado_en)}</td>
              <td style="padding:8px 12px;">
                ${p.comprobante_url ? '<span class="pf-texto-suave">Enviado</span>' : '—'}
              </td>
            </tr>`,
            )
            .join('')}
        </tbody>
      </table>
    </div>
  `;
}
